import React, { useState, useEffect } from 'react';
import { Table, Tag, Input, Button, Spin, Checkbox, Dropdown, Menu, Select, Modal } from 'antd';
import { getInvoices, getInvoicesFirstApproval, getInvoicesNewVendor } from '../../api/InvoiceContainer';
import { getVendors, getVendorTypes } from '../../api/VendorsContainer';
import {
    COMPLIANCE_CHECK_ROLE,
    FINANCIAL_CONTROLLER_ROLE,
    getFinancialControllers,
} from '../../api/FinancialControllersContainer';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'; // Import useHistory hook from React Router
import { useAuth } from '../../context/AuthContext';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

const { Option } = Select;
const { Search } = Input;

const DEFAULT_VISIBLE_COLUMNS = [
    'company', 'vendor', 'invoice_number',
    'invoice_date', 'due_date', 'amount', 'currency',
    'sender', 'pending', 'status', 'approved_date', 'agreement'
];
const DEFAULT_PAGE_SIZE = 10;

const parsePositiveInteger = (value, fallback) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const InvoiceHistory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchText, setSearchText] = useState(searchParams.get('search') || '');
    const [sortedInfo, setSortedInfo] = useState({
        columnKey: searchParams.get('sortColumn') || undefined,
        order: searchParams.get('sortOrder') || undefined,
    });
    const [invoiceData, setInvoiceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const columns = searchParams.get('columns');
        return columns ? columns.split(',').filter(Boolean) : DEFAULT_VISIBLE_COLUMNS;
    });
    const [tablePagination, setTablePagination] = useState({
        current: parsePositiveInteger(searchParams.get('page'), 1),
        pageSize: parsePositiveInteger(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE),
    });
    const { role, user } = useAuth();
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [vendorType, setVendorType] = useState(searchParams.get('vendorType') || 'All');

    const [vendorTypeOptions, setVendorTypeOptions] = useState([]);

    const updateHistoryParams = (updates) => {
        const nextParams = new URLSearchParams(searchParams);

        Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '' || value === 'All') {
                nextParams.delete(key);
                return;
            }

            nextParams.set(key, String(value));
        });

        setSearchParams(nextParams, { replace: true });
    };

    useEffect(() => {
        const fetchData = async () => {
            const vendorTypes = await getVendorTypes();
            setVendorTypeOptions(vendorTypes);
        };
        fetchData();
    }, []);

    const handleVendorTypeChange = (value) => {
        setVendorType(value);
        setTablePagination((current) => ({ ...current, current: 1 }));
        updateHistoryParams({ vendorType: value, page: 1 });
    };

    useEffect(() => {
        const fetchData = async () => {
            let data = null;
            if (role === 'Sender') {
                data = await getInvoices(user.name);
            }
            else if (role === 'Admin') {
                data = await getInvoices();
            }
            else if (role === 'Financial Controller') {
                data = await getInvoices();
            }
            else if (role === 'First Approver') {
                data = await getInvoicesFirstApproval(user.name);
            }
            else if (role === 'Sender or Accountant') {
                data = await getInvoices();
            }
            else if (role === 'First Approver & Compliance') {
                data = await getInvoicesNewVendor(user.name);
            }

            const vendorData = await getVendors();
            const controllers = await getFinancialControllers();
            const configuredFinancialController = controllers.find((controller) => controller.role === FINANCIAL_CONTROLLER_ROLE);
            const configuredComplianceCheckController = controllers.find((controller) => controller.role === COMPLIANCE_CHECK_ROLE);
            const vendorMap = vendorData?.reduce((acc, vendor) => {
                acc[vendor.id] = vendor.vendor;
                return acc;
            }, {});

            const formattedData = data?.map((invoice) => {
                const company = invoice.company || 'We Soda Ltd';

                // Format invoice_date
                const invoiceDate = new Date(invoice.invoice_date);
                const formattedInvoiceDate = `${invoiceDate.getDate()}/${invoiceDate.getMonth() + 1}/${invoiceDate.getFullYear()}`;

                // Format due_date if it exists
                let formattedDueDate = '';
                if (invoice?.due_date) {
                    const dueDate = new Date(invoice?.due_date);
                    formattedDueDate = `${dueDate.getDate()}/${dueDate.getMonth() + 1}/${dueDate.getFullYear()}`;
                }

                if (invoice?.paid_date) {
                    const paidDate = new Date(invoice?.paid_date);
                    invoice.paid_date = `${paidDate.getDate()}/${paidDate.getMonth() + 1}/${paidDate.getFullYear()}`;
                }

                if (invoice?.approved_date) {
                    const approvedDate = new Date(invoice?.approved_date);
                    invoice.approved_date = `${approvedDate.getDate()}/${approvedDate.getMonth() + 1}/${approvedDate.getFullYear()}`;
                }

                // Format amount
                const currencyCode = invoice?.currency === 'TL' ? 'TRY' : invoice?.currency === 'EURO' ? 'EUR' : 'GBP';
                const formattedAmount = parseFloat(invoice?.amount).toLocaleString('en-US', {
                    style: 'currency',
                    currency: currencyCode,
                });

                const invoiceType = vendorData.find(vendor => vendor.id === invoice.vendor_id)?.type;
                invoice.invoice_type = invoiceType;

                let pendingApproval = '';

                if (invoice?.status === 'Pending Compliance Check') {
                    pendingApproval = invoice?.compliance_check_controller || configuredComplianceCheckController?.controller || '';
                }

                if (invoice?.status === 'Pending First Approval' && invoice?.first_approver !== null) {
                    pendingApproval = invoice?.first_approver;
                }
                if (invoice?.status === 'Pending') {
                    pendingApproval = configuredFinancialController?.controller || '';
                }

                if (invoice?.status === 'Approved') {
                    pendingApproval = invoice?.approved_by
                }

                if (invoice?.status === 'Rejected') {
                    pendingApproval = invoice?.rejected_by
                }
                if (invoice?.status === 'Paid') {
                    pendingApproval = invoice?.paid_by
                }

                return {
                    ...invoice,
                    company,
                    currencyCode,
                    vendor: vendorMap[invoice?.vendor_id],
                    invoice_date: formattedInvoiceDate,
                    due_date: formattedDueDate,
                    amount: formattedAmount,
                    pending: pendingApproval,
                    invoice_type: invoiceType,
                    comment: invoice?.sender_comment,
                    approved_date: invoice?.approved_date || '',
                };
            });

            // sort the data by status Pending first 
            formattedData?.sort((a, b) => {
                if (a.status === 'Pending') {
                    return -1;
                }
                if (b.status === 'Pending') {
                    return 1;
                }
                if (a.status === 'Pending Compliance Check') {
                    return -1;
                }
                if (b.status === 'Pending Compliance Check') {
                    return 1;
                }
                if (a.status === 'Pending First Approval') {
                    return -1;
                }
                if (b.status === 'Pending First Approval') {
                    return 1;
                }
                if (a.status === 'Approved') {
                    return -1;
                }
                if (b.status === 'Approved') {
                    return 1;
                }
                if (a.status === 'Paid') {
                    return -1;
                }
                if (b.status === 'Paid') {
                    return 1;
                }

                return 0;
            });

            setInvoiceData(formattedData);
        };
        if (user) {
            fetchData()
                .then(() => setLoading(false));
        }
    }, [role, user]);

    const handleInvoiceClick = (invoice) => {
        if (role === 'Financial Controller' && (invoice.status === 'Pending First Approval' || invoice.status === 'Pending Compliance Check')) {
            return;
        }
        navigate(`/invoice-detail/${invoice.id}${location.search}`);
    };

    const allColumns = [
        {
            title: 'Company',
            dataIndex: 'company',
            key: 'company',
            sorter: (a, b) => a.company.localeCompare(b.company),
            sortOrder: sortedInfo.columnKey === 'company' && sortedInfo.order,
        },
        {
            title: 'Vendor',
            dataIndex: 'vendor',
            key: 'vendor',
            sorter: (a, b) => a.vendor.localeCompare(b.vendor),
            sortOrder: sortedInfo.columnKey === 'vendor' && sortedInfo.order,
        },
        {
            title: 'Invoice Number',
            dataIndex: 'invoice_number',
            key: 'invoice_number',
            sorter: (a, b) => a.invoice_number.localeCompare(b.invoice_number),
            sortOrder: sortedInfo.columnKey === 'invoice_number' && sortedInfo.order,
        },
        {
            title: 'Invoice Type',
            dataIndex: 'invoice_type',
            key: 'invoice_type',
            sorter: (a, b) => {
                const invoiceTypeA = a.invoice_type || ''; // If invoice_type is undefined, use an empty string
                const invoiceTypeB = b.invoice_type || ''; // If invoice_type is undefined, use an empty string
                return invoiceTypeA.localeCompare(invoiceTypeB);
            },
            sortOrder: sortedInfo.columnKey === 'invoice_type' && sortedInfo.order,
        },
        {
            title: 'Invoice Date',
            dataIndex: 'invoice_date',
            key: 'invoice_date',
            sorter: (a, b) => a.invoice_date?.localeCompare(b.invoice_date),
            sortOrder: sortedInfo.columnKey === 'invoice_date' && sortedInfo.order,
        },
        {
            title: 'Due Date',
            dataIndex: 'due_date',
            key: 'due_date',
            sorter: (a, b) => a?.due_date?.localeCompare(b?.due_date),
            sortOrder: sortedInfo.columnKey === 'due_date' && sortedInfo.order,
        },
        {
            title: 'Paid Date',
            dataIndex: 'paid_date',
            key: 'paid_date',
            sorter: (a, b) => a?.paid_date?.localeCompare(b?.paid_date),
            sortOrder: sortedInfo.columnKey === 'paid_date' && sortedInfo.order,
        },
        {
            title: 'Currency',
            dataIndex: 'currency',
            key: 'currency',
            sorter: (a, b) => a.currency.localeCompare(b.currency),
            sortOrder: sortedInfo.columnKey === 'currency' && sortedInfo.order,
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            sorter: (a, b) => {
                // Remove currency symbols and commas from the amount strings and parse as float
                const amountA = parseFloat((a.amount || '').replace(/[^0-9.-]+/g, '')); // Get the number value from amount A
                const amountB = parseFloat((b.amount || '').replace(/[^0-9.-]+/g, '')); // Get the number value from amount B

                // Check for NaN or undefined values
                if (isNaN(amountA) || isNaN(amountB) || amountA === undefined || amountB === undefined) {
                    return 0; // Return 0 if amounts are NaN or undefined
                }

                return amountA - amountB;
            },
            sortOrder: sortedInfo.columnKey === 'amount' && sortedInfo.order,
        },
        {
            title: 'Sender',
            dataIndex: 'sender',
            key: 'sender',
            sorter: (a, b) => a.sender.localeCompare(b.sender),
            sortOrder: sortedInfo.columnKey === 'sender' && sortedInfo.order,
        },
        {
            title: 'Pending/Approved',
            dataIndex: 'pending',
            key: 'pending',
            sorter: (a, b) => a.pending?.localeCompare(b.pending),
            sortOrder: sortedInfo.columnKey === 'pending' && sortedInfo.order,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const color = tagColorHandler(status);
                return (
                    <Tag color={color} key={status}>
                        {status?.toUpperCase()}
                    </Tag>
                );
            },
            sorter: (a, b) => a.status.localeCompare(b.status),
            sortOrder: sortedInfo.columnKey === 'status' && sortedInfo.order,
        },
        {
            title: 'Approval Date',
            dataIndex: 'approved_date',
            key: 'approved_date',
            sorter: (a, b) => a.approved_date?.localeCompare(b.approved_date),
            sortOrder: sortedInfo.columnKey === 'approved_date' && sortedInfo.order,
        },
        {
            title: 'Agreement',
            dataIndex: 'agreement',
            key: 'agreement',
            sorter: (a, b) => a.agreement?.localeCompare(b.agreement),
            sortOrder: sortedInfo.columnKey === 'agreement' && sortedInfo.order,
        }
    ];

    const handleTableChange = (pagination, filters, sorter) => {
        const nextSorter = Array.isArray(sorter) ? sorter[0] || {} : sorter;
        const appliedSorter = nextSorter?.order ? nextSorter : {};
        const nextPagination = {
            current: pagination?.current || 1,
            pageSize: pagination?.pageSize || DEFAULT_PAGE_SIZE,
        };

        setTablePagination(nextPagination);
        setSortedInfo(appliedSorter); // Update sortedInfo state with the current sorting information
        updateHistoryParams({
            page: nextPagination.current,
            pageSize: nextPagination.pageSize,
            sortColumn: appliedSorter?.columnKey,
            sortOrder: appliedSorter?.order,
        });
    };

    const tagColorHandler = (tag) => {
        switch (tag) {
            case 'Pending':
                return 'orange';
            case 'Approved':
                return 'green';
            case 'Rejected':
                return 'red';
            case 'Pending Compliance Check':
                return 'cyan';
            case 'Pending First Approval':
                return 'blue';
            case 'Paid':
                return 'purple';
            default:
                return 'default';
        }
    };

    const filteredData = invoiceData?.filter((invoice) =>
        Object.values(invoice).some((value) => {
            if (value === null || value === undefined) {
                return false; // Skip null or undefined values
            }
            return value.toString().toLowerCase().includes(searchText.toLowerCase());
        })
    );

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchText(value);
        setTablePagination((current) => ({ ...current, current: 1 }));
        updateHistoryParams({ search: value, page: 1 });
    };

    const exportToCSV = () => {
        // Map your filtered data to CSV columns
        const csvData = filteredData.map(row => ({
            Company: row.company,
            Vendor: row.vendor,
            'Invoice Number': row.invoice_number,
            'Invoice Type': row.invoice_type,
            'Invoice Date': row.invoice_date,
            'Due Date': row.due_date,
            'Paid Date': row.paid_date,
            Amount: // Remove currency symbols
                parseFloat((row.amount || '').replace(/[^0-9.-]+/g, '')) || 0, // Parse as float or default to 0
            Currency: row.currency,
            Sender: row.sender,
            Comment: row.comment,
            'Pending': row.pending_approval,
            Status: row.status,
            'Approval Date': row.approved_date,
            Agreement: row.agreement,
        }));

        // Use PapaParse to convert data to CSV format
        const csv = Papa.unparse(csvData);

        // Create a Blob object with CSV content and set UTF-8 encoding
        const blob = new Blob([csv], { type: 'data:text/csv;charset=utf-8,%EF%BB%BF;' });

        // Generate a download link and trigger download programmatically
        const today = new Date().toISOString().split('T')[0];
        saveAs(blob, `invoices-${today}.csv`);
    };

    const handleExport = () => {
        if (vendorType === 'All') {
            exportToCSV();
        } else {
            const filteredDataByVendorType = filteredData.filter(invoice => invoice.invoice_type === vendorType);

            // Map your filtered data to CSV columns
            const csvData = filteredDataByVendorType.map(row => ({
                Company: row.company,
                Vendor: row.vendor,
                'Invoice Number': row.invoice_number,
                'Invoice Type': row.invoice_type,
                'Invoice Date': row.invoice_date,
                'Due Date': row.due_date,
                'Paid Date': row.paid_date,
                Amount: // Remove currency symbols
                    parseFloat((row.amount || '').replace(/[^0-9.-]+/g, '')) || 0, // Parse as float or default to 0
                Currency: row.currency,
                Sender: row.sender,
                Comment: row.comment,
                'Pending': row.pending_approval,
                Status: row.status,
                'Approval Date': row.approved_date,
                Agreement: row.agreement,
            }));

            // Use PapaParse to convert data to CSV format
            const csv = Papa.unparse(csvData);

            // Create a Blob object with CSV content and set UTF-8 encoding
            const blob = new Blob([csv], { type: 'data:text/csv;charset=utf-8,%EF%BB%BF;' });

            // Generate a download link and trigger download programmatically
            const today = new Date().toISOString().split('T')[0];
            saveAs(blob, `invoices-${vendorType}-${today}.csv`);
        }
    };


    const handleColumnChange = (checkedValues) => {
        setVisibleColumns(checkedValues);
        updateHistoryParams({ columns: checkedValues.join(',') });
    };

    const columnCheckboxOptions = allColumns.map(column => ({
        label: column.title,
        value: column.key
    }));
    const pendingFinancialControllerCount = invoiceData?.filter((invoice) => invoice.status === 'Pending').length || 0;
    const pendingComplianceCheckCount = invoiceData?.filter((invoice) => invoice.status === 'Pending Compliance Check').length || 0;
    const pendingFirstApprovalCount = invoiceData?.filter((invoice) => invoice.status === 'Pending First Approval').length || 0;
    const approvedInvoiceCount = invoiceData?.filter((invoice) => invoice.status === 'Approved').length || 0;
    const statusMetrics = [
        {
            label: 'Pending Compliance Check',
            value: pendingComplianceCheckCount,
        },
        {
            label: 'Pending First Approval',
            value: pendingFirstApprovalCount,
        },
        {
            label: 'Pending Financial Controller',
            value: pendingFinancialControllerCount,
        },
        {
            label: 'Approved',
            value: approvedInvoiceCount,
        },
    ].filter((metric) => metric.value > 0);

    if (loading) {
        return <Spin
            size="large"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flex: 1, marginTop: -100 }}
        />;
    }

    return (
        <div className="invoice-history-page">
            <div className="page-heading-row">
                <div className="page-heading-copy">
                    <span className="page-kicker">Invoices</span>
                    <h1>Invoice History</h1>
                </div>
                {statusMetrics.length > 0 && (
                    <div className="page-metrics-panel">
                        <span className="page-metrics-title">Status Summary</span>
                        <div className="page-metrics">
                            {statusMetrics.map((metric) => (
                                <div className="page-metric" key={metric.label}>
                                    <span className="page-metric-value">{metric.value}</span>
                                    <span className="page-metric-label">{metric.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="invoice-history-toolbar">
                <div className="invoice-history-search">
                    <Search
                        allowClear
                        placeholder="Search invoices"
                        onChange={handleSearchChange}
                        value={searchText}
                    />
                </div>
                <div className="invoice-history-actions">
                    <Button type="primary" onClick={() => setExportModalVisible(true)}>
                        Export to CSV
                    </Button>
                    <Dropdown
                        overlay={
                            <Menu>
                                <Checkbox.Group
                                    className="column-checkbox-group"
                                    options={columnCheckboxOptions}
                                    value={visibleColumns}
                                    onChange={handleColumnChange}
                                />
                            </Menu>
                        }
                        trigger={['click']}
                    >
                        <Button>Columns</Button>
                    </Dropdown>
                </div>
            </div>

            <div className="invoice-table-shell">
                <Table
                    className="invoice-history-table"
                    columns={allColumns.filter(column => visibleColumns.includes(column.key))}
                    dataSource={filteredData}
                    onChange={handleTableChange}
                    onRow={(record) => ({ onClick: () => handleInvoiceClick(record) })}
                    pagination={tablePagination}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                />
            </div>
            <Modal
                title="CSV Export"
                open={exportModalVisible}
                onOk={() => { handleExport(); setExportModalVisible(false); }}
                onCancel={() => setExportModalVisible(false)}
            >
                <p className="modal-helper-copy">Select vendor type to export invoices.</p>
                <Select
                    id="vendorType"
                    showSearch  // Enable search functionality
                    value={vendorType}
                    onChange={handleVendorTypeChange}
                    style={{ width: '100%', height: 40 }}
                    placeholder="Select Vendor Type"
                    optionFilterProp="children"  // Specify the property to filter options on
                    filterOption={(input, option) =>
                        option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }  // Filter options based on input
                    required
                >
                    {vendorTypeOptions.map((option) => (
                        <Option key={option.id} value={option.type}>
                            {option.type}
                        </Option>
                    ))}
                </Select>

            </Modal>
        </div>
    );
};

export default InvoiceHistory;
